import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { API_BASE_URL } from '../config/api-config';
import { getStoredLicenseKeyHash, clearStoredLicenseKeyHash, storeUserEmail } from '../services/LicenseService';

// Types
export interface User {
  userId: number | null;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  customerId: string | null;
  customerName: string | null;
  customerRole: string | null;
  authorities: string[];
}

export interface AuthState {
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // User information
  user: User | null;

  // Customer/tenant information
  customerId: string | null;

  // Development mode flag
  isDevelopmentMode: boolean;
}

// Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'SET_DEVELOPMENT_MODE'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true, // Start loading to check auth status
  error: null,
  user: null,
  customerId: null,
  isDevelopmentMode: false,
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        user: action.payload,
        customerId: action.payload.customerId,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        user: null,
        customerId: null,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        user: null,
        customerId: null,
      };

    case 'SET_DEVELOPMENT_MODE':
      return {
        ...state,
        isDevelopmentMode: action.payload,
        // In development mode, we don't require authentication
        isLoading: action.payload ? false : state.isLoading,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Result of a direct sign-in attempt, surfaced to the dialog for inline display.
export interface DirectLoginResult {
  success: boolean;
  message?: string;
  errorCode?: string;
}

// Context
interface AuthContextType {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  checkAuth: (silent?: boolean) => Promise<void>;
  directLogin: (email: string, password: string) => Promise<DirectLoginResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication status.
  //
  // silent=true revalidates WITHOUT dispatching AUTH_START. AUTH_START sets
  // isLoading, and AuthGatedWorkflow swaps the workflow panel for a loading
  // skeleton while isLoading — unmounting PDFUpload and discarding its local
  // state (selected file, workflow step). The window-focus re-check must be
  // silent: the OS file picker itself blurs/refocuses the window, so a loud
  // re-check destroyed the just-selected file before it could be uploaded.
  // A silent check still dispatches on a DEFINITIVE outcome (signed in/out),
  // so cross-tab sign-in/sign-out sync keeps working; it stays quiet on
  // network errors rather than signing the UI out over a transient blip.
  const checkAuth = useCallback(async (silent: boolean = false) => {
    if (!silent) {
      dispatch({ type: 'AUTH_START' });
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check`, {
        method: 'GET',
        credentials: 'include', // Include cookies for session
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        // If we get a redirect to login or an error, user is not authenticated
        if (response.status === 401 || response.status === 403) {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Not authenticated' });
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.authenticated) {
        // Fetch full user info
        const userResponse = await fetch(`${API_BASE_URL}/api/auth/user`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          // Persist the email so non-context callers (e.g. session save) can attach it as a header.
          storeUserEmail(userData.email);
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              userId: userData.userId,
              email: userData.email,
              displayName: userData.displayName,
              firstName: userData.firstName,
              lastName: userData.lastName,
              customerId: userData.customerId,
              customerName: userData.customerName,
              customerRole: userData.customerRole,
              authorities: userData.authorities || [],
            },
          });
        } else {
          // Basic auth success without full user data
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              userId: null,
              email: null,
              displayName: data.username || 'User',
              firstName: null,
              lastName: null,
              customerId: data.customerId,
              customerName: null,
              customerRole: null,
              authorities: [],
            },
          });
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Not authenticated' });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // A network/server error is not a definitive auth answer. A silent
      // background re-check keeps the current state rather than signing the
      // UI out (and wiping editor state) over a transient blip; the initial
      // (loud) check must still resolve the loading gate to a failure.
      if (silent) {
        return;
      }
      // Only fall back to development mode when REACT_APP_ENV is actually
      // 'development'. In qa/production a network failure here is a real
      // outage and should surface as an auth failure, not silently flip the
      // app into a permissive mode that exposes a misleading "Dev Mode"
      // indicator.
      if (process.env.REACT_APP_ENV === 'development') {
        dispatch({ type: 'SET_DEVELOPMENT_MODE', payload: true });
      }
      dispatch({ type: 'AUTH_FAILURE', payload: 'Could not connect to server' });
    }
  }, []);

  // Direct sign-in: POST credentials + license-key-hash to the PFB backend,
  // which verifies credentials server-to-server with LS, runs the license
  // ownership checks, and sets the jwt HttpOnly cookie — no browser redirect
  // to LS's hosted login page. The unified sign-in dialog orchestrates this
  // after (optionally) applying a license key with the same user input.
  const directLogin = useCallback(async (email: string, password: string): Promise<DirectLoginResult> => {
    const licenseKeyHash = getStoredLicenseKeyHash();
    if (!licenseKeyHash) {
      return { success: false, message: 'Please enter a license key before signing in' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // jwt cookie is set on this response
        body: JSON.stringify({ email, password, licenseKeyHash }),
      });

      const data = await response.json().catch(() => ({} as { success?: boolean; message?: string; error?: string }));

      if (!response.ok || !data.success) {
        return {
          success: false,
          message: data.message || `Sign-in failed (status ${response.status})`,
          errorCode: data.error,
        };
      }

      storeUserEmail(email);
      await checkAuth();  // populate user state from the freshly set jwt cookie

      // Signing in changes what this session MAY DO, not just who it is: a guest
      // becomes a licensed user, which turns on the session library, the artifact
      // downloads and the real "Your Session" panel. CapabilitiesContext and
      // LicenseContext both listen on this bus and have no other way to hear
      // about it.
      //
      // IT HAS TO FIRE HERE, not in the sign-in dialog, because both of the
      // dialog's paths miss it:
      //   - signing in with a licence key already applied enters no key, so
      //     applyLicenseKey never runs and nothing is dispatched at all;
      //   - signing in WITH a new key dispatches from applyLicenseKey at stage 1,
      //     BEFORE this login completes, so the refresh it triggers still sees an
      //     unauthenticated session and caches the guest answer.
      // Dispatching on the authentication itself covers both. (2026-09-07)
      window.dispatchEvent(new CustomEvent('license-changed'));

      return { success: true };
    } catch (err) {
      console.error('direct login network error:', err);
      return { success: false, message: 'Cannot connect to server. Please try again.' };
    }
  }, [checkAuth]);

  // Logout — OIDC RP-initiated logout.
  //
  // Top-level navigation (NOT fetch) is required so the browser follows the
  // full server-redirect chain:
  //   1. GET /logout (Spring LogoutFilter; CSRF disabled for /logout so GET is accepted) — clears PFB JWT + JSESSIONID
  //   2. 302 → LS /connect/logout?id_token_hint=...&post_logout_redirect_uri=...
  //      LS clears its session cookie (the SSO cookie that was silently
  //      re-authenticating the previous user)
  //   3. 302 → ${APP_FRONTEND_URL}/ (the PFB home page)
  //
  // A fetch() would receive opaque CORS redirects and never clear LS's cookie.
  // The local state dispatch is unnecessary because the page is about to
  // reload via the redirect.
  const logout = useCallback(async () => {
    // Clear the cached license-key-hash before redirecting. Without this, a
    // different customer's user signing in on the same browser would inherit
    // the prior license's hash via localStorage and the X-License-Key-Hash
    // header would route their data queries to the prior customer's tenant.
    // See memory project_cross_customer_bleed_on_signin.
    clearStoredLicenseKeyHash();
    storeUserEmail(null);
    window.location.href = `${API_BASE_URL}/logout`;
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check when the tab regains focus. The jwt cookie is shared across
  // tabs of the same browser context, so signing in (or out) in one tab
  // otherwise leaves other tabs — and the Getting Started guide in them —
  // acting on stale auth state until a manual reload.
  //
  // MUST be silent (see checkAuth): the OS file picker blurs and refocuses
  // the window, and a loud re-check unmounts the workflow mid-selection.
  useEffect(() => {
    const onFocus = () => { checkAuth(true); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ state, dispatch, checkAuth, directLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Convenience hooks
export const useUser = () => {
  const { state } = useAuth();
  return state.user;
};

export const useCustomerId = () => {
  const { state } = useAuth();
  return state.customerId;
};

export const useIsAuthenticated = () => {
  const { state } = useAuth();
  return state.isAuthenticated;
};

// Action creators for convenience
export const authActions = {
  startAuth: () => ({ type: 'AUTH_START' as const }),
  authSuccess: (user: User) => ({ type: 'AUTH_SUCCESS' as const, payload: user }),
  authFailure: (error: string) => ({ type: 'AUTH_FAILURE' as const, payload: error }),
  logout: () => ({ type: 'AUTH_LOGOUT' as const }),
  setDevelopmentMode: (isDev: boolean) => ({ type: 'SET_DEVELOPMENT_MODE' as const, payload: isDev }),
  clearError: () => ({ type: 'CLEAR_ERROR' as const }),
};
